import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSliders } from "@fortawesome/free-solid-svg-icons";
import Layout from "../../components/layout/layout";
import axiosInstance from "../../AxiosInstance";
import usePolling from "../../hooks/usePolling";
import { useSidebar } from "../../contexts/SidebarContext";
import RoadCells from "./components/road_cells";
import RoadControls from "./components/road_controls";
import RoadLegend from "./components/road_legend";
import RoadKpi from "./components/road_kpi";
import RoadTimeline from "./components/road_timeline";
import RoadChanges from "./components/road_changes";
import RoadImpacts from "./components/road_impacts";
import styles from "../network_map/network_map.module.css";

const POLL_MS = 60000;
const PREFS_KEY = "pdui_roadmap_prefs";

function loadPrefs() {
  try {
    return JSON.parse(window.localStorage.getItem(PREFS_KEY) || "{}");
  } catch (e) {
    return {};
  }
}

function savePrefs(prefs) {
  try {
    window.localStorage.setItem(PREFS_KEY, JSON.stringify(prefs));
  } catch (e) {
    // sin storage no se recuerda
  }
}

/**
 * Estado del camino: celdas coloreadas por la vibración vertical medida en
 * los camiones (índice 0-100), evolución, tramos que cambiaron y golpes.
 */
export default function RoadMap() {
  const { t } = useTranslation();
  const { isMobile } = useSidebar();
  const prefs = useMemo(loadPrefs, []);
  const [metric, setMetric] = useState(prefs.metric || "score");
  const [days, setDays] = useState(prefs.days || 7);
  const [cellM, setCellM] = useState(prefs.cellM || 50);
  const [pdui, setPdui] = useState("all");
  const [allSpeeds, setAllSpeeds] = useState(false);
  const [showChanges, setShowChanges] = useState(true);
  const [showImpacts, setShowImpacts] = useState(true);
  const [controlsOpen, setControlsOpen] = useState(false);
  const [panelOpen, setPanelOpen] = useState(!isMobile);
  const [tab, setTab] = useState("timeline");
  const [heatmap, setHeatmap] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [timeline, setTimeline] = useState(null);
  const [changes, setChanges] = useState(null);
  const [changesError, setChangesError] = useState(false);
  const [impacts, setImpacts] = useState(null);
  const [impactsError, setImpactsError] = useState(false);
  const [focus, setFocus] = useState(null);
  const [dismissedBanner, setDismissedBanner] = useState(false);

  useEffect(() => {
    savePrefs({ metric, days, cellM });
  }, [metric, days, cellM]);

  const query = `road_heatmap/?days=${days}&cell=${cellM}&pdui=${pdui}${allSpeeds ? "&all_speeds=1" : ""}`;
  usePolling(
    (isActive) =>
      axiosInstance
        .get(query)
        .then((r) => {
          if (!isActive()) return;
          setHeatmap(r.data);
          setError(false);
        })
        .catch(() => {
          if (isActive()) setError(true);
        })
        .finally(() => {
          if (isActive()) setLoading(false);
        }),
    POLL_MS,
    true,
    query,
  );

  const reloadAnalysis = useCallback(() => {
    const speeds = allSpeeds ? "&all_speeds=1" : "";
    axiosInstance.get(`road_changes/?cell=${cellM}&pdui=${pdui}${speeds}`).then((r) => { setChanges(r.data); setChangesError(false); }).catch(() => setChangesError(true));
    axiosInstance.get(`road_timeline/?days=${days}&pdui=${pdui}${speeds}`).then((r) => setTimeline(r.data)).catch(() => setTimeline(null));
    axiosInstance.get(`road_impacts/?days=${days}&pdui=${pdui}`).then((r) => { setImpacts(r.data.impacts || []); setImpactsError(false); }).catch(() => setImpactsError(true));
  }, [cellM, pdui, days, allSpeeds]);

  useEffect(() => {
    reloadAnalysis();
  }, [reloadAnalysis]);

  const cells = heatmap?.cells || [];
  const windowCount = heatmap?.summary?.n || 0;
  const noData = !loading && !error && cells.length === 0;
  const showBanner = (noData || error) && !dismissedBanner;
  const goTo = (item) => {
    setFocus({ ...item, _ts: Date.now() });
    if (isMobile) setPanelOpen(false);
  };

  return (
    <Layout selectedIndex={-1}>
      <div className={styles.page}>
        <div className={styles.mapArea}>
          <RoadCells center={heatmap?.center} cells={cells} metric={metric} devices={heatmap?.devices || []} changes={changes}
            showChanges={showChanges} impacts={impacts || []} showImpacts={showImpacts} focus={focus} />
          {loading && <div className={styles.loadingDot}>…</div>}
          <div className={styles.topStack}>
            <RoadKpi summary={heatmap?.summary} days={days} />
            {showBanner && (
              <div className={`${styles.banner} ${error ? styles.bannerError : ""}`} role="status">
                <button type="button" className={styles.bannerClose} onClick={() => setDismissedBanner(true)} aria-label={t("roadmap.dismiss")} title={t("roadmap.dismiss")}>×</button>
                {error ? t("roadmap.loadError") : windowCount > 0 ? t("roadmap.noGps", { n: windowCount }) : t("roadmap.noData")}
                {!error && <div className={styles.bannerHint}>{windowCount > 0 ? t("roadmap.noGpsHint") : t("roadmap.noDataHint")}</div>}
              </div>
            )}
          </div>
          <button type="button" className={styles.fab} onClick={() => setControlsOpen(true)}>
            <FontAwesomeIcon icon={faSliders} /> {t("roadmap.filters")}
          </button>
          <RoadControls open={controlsOpen} onClose={() => setControlsOpen(false)} metric={metric} setMetric={setMetric} days={days} setDays={setDays}
            cellM={cellM} setCellM={setCellM} pdui={pdui} setPdui={setPdui} devices={heatmap?.devices || []} allSpeeds={allSpeeds} setAllSpeeds={setAllSpeeds}
            showChanges={showChanges} setShowChanges={setShowChanges} showImpacts={showImpacts} setShowImpacts={setShowImpacts}
            cellCount={cells.length} windowCount={windowCount} />
          <RoadLegend metric={metric} />
        </div>
        <div className={`${styles.panel} ${panelOpen ? "" : styles.panelClosed}`}>
          <div className={styles.panelBar}>
            <div className={styles.changeTabs}>
              {["timeline", "changes", "impacts"].map((key) => (
                <button key={key} type="button" className={`${styles.tab} ${tab === key ? styles.tabActive : ""}`} onClick={() => { setTab(key); setPanelOpen(true); }}>
                  {t(`roadmap.tabs.${key}`)}
                </button>
              ))}
            </div>
            <button type="button" className={styles.panelToggle} onClick={() => setPanelOpen((o) => !o)}>
              {panelOpen ? t("roadmap.hidePanel") : t("roadmap.showPanel")} {panelOpen ? "⌄" : "⌃"}
            </button>
          </div>
          {panelOpen && (
            <div className={styles.panelBody}>
              {tab === "timeline" && <RoadTimeline points={timeline?.points} bucket={timeline?.bucket} metric={metric === "speed_kmh" ? "score" : metric} />}
              {tab === "changes" && <RoadChanges changes={changes} error={changesError} onGo={goTo} />}
              {tab === "impacts" && <RoadImpacts impacts={impacts} error={impactsError} onGo={goTo} />}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
